import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { camelCaseToWords, capitalizeAllWordsInString } from 'utils/Strings';
import { SpecialField, specialFields } from '../constants';
import { useMemo } from 'react';
import { useHrisCustomFieldsFlag } from './useHrisCustomFieldsFlag';

interface CustomField {
  key: string;
  label: string;
}

export const useOptionalFields = () => {
  const isHrisCustomFieldsEnabled = useHrisCustomFieldsFlag();
  const optionalPersonalFields = useSelector(
    loggedInUserSelectors.currentOrganization,
  )?.optionalUserData;

  // When HRIS custom fields flag is ON, legacy optional fields are replaced by
  // the Additional Details step. Return empty collections so that:
  // 1. No legacy optional field rows render in Personal Details / Review sections.
  // 2. optionalEmployeeData in the payrollSelfOnboarding payload evaluates to {}
  //    (the reduce below over an empty array) — backend accepts an empty object fine.
  const optionalFieldsKeys = isHrisCustomFieldsEnabled
    ? []
    : Object.keys(optionalPersonalFields ?? {}).filter((key) => {
        return !!optionalPersonalFields?.[key];
      });

  const optionalFields = optionalFieldsKeys.reduce((acc: Record<string, CustomField>, key) => {
    if (typeof optionalPersonalFields?.[key] === 'string') {
      acc[key] = {
        key,
        label: optionalPersonalFields[key] as string,
      };
    } else {
      acc[key] = {
        key,
        label: capitalizeAllWordsInString(camelCaseToWords(key)),
      };
    }

    return acc;
  }, {});

  const regularFieldsKeys = optionalFieldsKeys.filter((key: string) => {
    return !specialFields.includes(key as SpecialField);
  });

  const memoisedValues = useMemo(() => {
    return { optionalFieldsKeys, optionalFields, regularFieldsKeys };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionalPersonalFields, isHrisCustomFieldsEnabled]);

  return memoisedValues;
};
